/**
 * Analytics wrapper for PostHog.
 *
 * Provides a thin abstraction over posthog-js for:
 * - Initialization with CookieHub consent integration
 * - User identification with group analytics
 * - Event tracking with automatic common properties
 * - Consent-aware opt-in/opt-out
 */
import posthog from 'posthog-js'

// #region Module state

let initStarted = false
let initialized = false
// Tracks the latest consent state we've heard from CookieHub. CookieHub may
// dispatch its onInitialise event before PostHog finishes loading, so we
// stash the value and apply it once PostHog is ready.
let pendingConsent = null
// Tracks whether the active dealer group was set at login (session-wide,
// for single-dealer dealer users) versus per-page (e.g. WPC users viewing
// a specific dealer). Route navigation clears per-page groups but preserves
// session-wide ones.
let sessionDealerGroupActive = false
// Queue of analytics calls made AFTER consent was granted but BEFORE the
// PostHog SDK finished loading. This solves the SDK race for already-
// consented users (e.g. returning users on a slow network) without
// retaining or replaying any pre-consent activity.
const pendingCalls = []
// Group keys (`${type}:${key}`) we've already sent properties for this
// session. posthog.group() with properties emits a billed $groupidentify
// event; re-sending it on every dealer-page navigation is pure waste. We
// send properties once, then re-associate without them. Cleared on reset().
const identifiedGroups = new Set()

// Calls are gated until both the SDK has loaded AND consent has been granted.
const canSend = () => initialized && pendingConsent === true

// #endregion

// #region Consent + queue lifecycle

function queueOrCall(fn) {
  if (canSend()) {
    fn()
    return
  }
  // Consent boundary: only queue when consent is already known granted.
  // Calls before consent is known (or after denial) are dropped, never replayed.
  if (pendingConsent === true) {
    pendingCalls.push(fn)
  }
}

function flushPendingCalls() {
  const calls = pendingCalls.splice(0)
  calls.forEach((fn) => fn())
}

// Push the latest known CookieHub consent state into PostHog. Idempotent.
// Opt-in upgrades persistence so the distinct ID survives reloads and flushes
// any queued calls; opt-out drops the queue and any prior-session cookies.
function syncConsent(ph) {
  if (pendingConsent === null) return
  if (pendingConsent) {
    ph.set_config({ persistence: 'localStorage+cookie' })
    // captureEventName: false suppresses the default `$opt_in` event.
    // CookieHub re-dispatches consent on every load, so without this each
    // already-consented page load would emit a low-signal $opt_in.
    ph.opt_in_capturing({ captureEventName: false })
    flushPendingCalls()
  } else {
    ph.opt_out_capturing({ clear_persistence: true })
    ph.set_config({ persistence: 'memory' })
    pendingCalls.length = 0
  }
}

// #endregion

// #region Identity helpers

function buildPersonProperties(user) {
  const role = user.ssoUserProfile?.role || user.dealerUserProfile?.role || null
  const props = {
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
    user_type: user.userType,
    role,
    created_at: user.createdAt,
  }

  if (user.dealerUserProfile) {
    const dealers = user.dealerUserProfile.dealers || []
    const dealerGroups = user.dealerUserProfile.dealerGroups || []
    props.dealer_ids = dealers.map((d) => d.id)
    props.dealer_names = dealers.map((d) => d.name)
    props.dealer_group_ids = dealerGroups.map((g) => g.id)
    props.dealer_count = dealers.length
    props.self_managed = user.isSelfManaged === true
  }

  return props
}

// Associate the current user with a group, sending properties ($groupidentify)
// only the first time we see the group this session. Subsequent calls just
// re-associate — group(type, key) with no properties does not emit
// $groupidentify, so navigation between dealer pages is free.
function setGroupOnce(type, key, properties) {
  const cacheKey = `${type}:${key}`
  if (identifiedGroups.has(cacheKey)) {
    posthog.group(type, key)
    return
  }
  posthog.group(type, key, properties)
  identifiedGroups.add(cacheKey)
}

function applyDealerGroup(dealer) {
  setGroupOnce('dealer', dealer.id, {
    name: dealer.name,
    dealer_group_id: dealer.group?.id,
    dealer_group_name: dealer.group?.name,
  })

  if (dealer.group?.id) {
    setGroupOnce('dealer_group', dealer.group.id, {
      name: dealer.group.name,
    })
  }
}

// #endregion

// #region Public API

/**
 * Initialize PostHog with the configured project key and host.
 * Integrates with CookieHub consent management.
 */
export function init() {
  // Idempotent — production boots once but HMR / tests re-evaluate the module.
  if (initStarted) return
  const key = import.meta.env.VITE_POSTHOG_KEY
  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

  if (!key) return
  initStarted = true

  const cookiehubEnabled = import.meta.env.VITE_COOKIEHUB_ENABLED === 'true'

  // Listener must be attached BEFORE posthog.init so we don't miss the
  // cookiehub_consent_changed event when CookieHub initializes faster than
  // the PostHog SDK loads. The `loaded` callback below applies the value.
  if (typeof window !== 'undefined') {
    window.addEventListener('cookiehub_consent_changed', (e) => {
      pendingConsent = !!e?.detail?.categories?.analytics
      if (initialized) syncConsent(posthog)
    })
  }

  // No CookieHub in this env → no consent flow → grant immediately, otherwise
  // pendingConsent stays null forever and nothing ever fires.
  if (!cookiehubEnabled) {
    pendingConsent = true
  }

  const sessionRecordingEnabled = import.meta.env.VITE_POSTHOG_SESSION_RECORDING_ENABLED === 'true'
  const autocaptureEnabled = import.meta.env.VITE_POSTHOG_AUTOCAPTURE_ENABLED === 'true'
  const pageviewEnabled = import.meta.env.VITE_POSTHOG_PAGEVIEW_ENABLED === 'true'

  posthog.init(key, {
    api_host: host,
    // Only create/update person profiles for identified (logged-in) users.
    // Pre-login traffic stays anonymous, which uses anonymous events
    person_profiles: 'identified_only',
    // Pageviews are gated separately from autocapture so we can keep
    // lightweight page analytics without the per-click event volume.
    // 'history_change' (not `true`) is required for posthog-js to capture
    // pageviews on SPA route changes, not just the initial page load.
    capture_pageview: pageviewEnabled ? 'history_change' : false,
    capture_pageleave: pageviewEnabled,
    autocapture: autocaptureEnabled,
    // Enable if using the Core Web Vitals dashboards
    capture_performance: { web_vitals: false },
    // Disable default/remote-config-toggleable capture products we don't use
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    disable_session_recording: !sessionRecordingEnabled,
    // CookieHub already honors DNT via its category defaults; leaving
    // respect_dnt true here would double-block and prevent banner opt-in.
    respect_dnt: false,
    ip: false,
    // Start in memory mode so no cookies are set pre-consent. syncConsent()
    // flips to localStorage+cookie when CookieHub reports analytics consent.
    persistence: 'memory',
    opt_out_capturing_by_default: true,
    session_recording: {
      maskTextSelector: '[data-mask]',
      maskAllInputs: true,
    },
    loaded: (ph) => {
      initialized = true
      // Apply consent if it already arrived; otherwise the listener will.
      if (pendingConsent !== null) syncConsent(ph)
    },
  })
}

/**
 * Identify the current user and set person properties.
 *
 * Single-dealer dealer users get their dealer pinned as a session-wide group
 * so every event in the app is attributed to it. Multi-dealer and WPC users
 * rely on per-page setDealerGroup() instead.
 *
 * Re-fires mid-session (Settings, UserProfile, NavBar all call getUpdatedUser
 * → identify), so transitions in dealer membership must reset stale group
 * state — otherwise a session-wide group could outlive the membership that
 * justified it.
 *
 * @param {object} user - The user object from UserStore
 */
export function identify(user) {
  if (!user?.id) return

  queueOrCall(() => {
    posthog.identify(user.id, buildPersonProperties(user))

    if (user.dealerUserProfile) {
      const dealers = user.dealerUserProfile.dealers || []
      const wasSessionDealer = sessionDealerGroupActive
      sessionDealerGroupActive = false

      if (dealers.length === 1) {
        // Wipe stale session/per-page group state, then re-establish.
        posthog.resetGroups()
        applyDealerGroup(dealers[0])
        sessionDealerGroupActive = true
      } else if (wasSessionDealer) {
        // single-dealer → multi-dealer: drop the now-stale session group.
        posthog.resetGroups()
      }
    }
  })
}

/**
 * Set the active dealer (and dealer_group) as the PostHog group for subsequent
 * events. Call when entering a dealer-scoped page (DealerDetails, RepairOrder,
 * BillingDetails). Always pair with clearDealerGroup() on leave, or the group
 * will stick to events on unrelated screens.
 *
 * @param {object} dealer - Dealer object with id, name, and optional group
 */
export function setDealerGroup(dealer) {
  if (!dealer?.id) return
  queueOrCall(() => {
    // resetGroups() is the only way to clear a group_type slot — posthog.group()
    // can set but never unset. Without it, switching from a dealer with a
    // dealer_group to one without would leave the previous dealer_group stuck.
    // Single-dealer users skip this: their session-wide group must survive nav.
    if (!sessionDealerGroupActive) posthog.resetGroups()
    applyDealerGroup(dealer)
  })
}

/**
 * Clear the per-page PostHog group association. Call when leaving a
 * dealer-scoped route. No-op for single-dealer dealer users — their
 * session-wide attribution is correct everywhere and must not be cleared.
 */
export function clearDealerGroup() {
  queueOrCall(() => {
    if (sessionDealerGroupActive) return
    posthog.resetGroups()
  })
}

/**
 * Track an analytics event.
 *
 * @param {string} event - Event name (use constants from analytics.constants.js)
 * @param {object} [properties={}] - Event properties
 */
export function track(event, properties = {}) {
  queueOrCall(() => posthog.capture(event, properties))
}

/**
 * Reset the current user identity. Call on logout.
 *
 * Unconditionally clears the queue so prior-session analytics can't leak
 * across logout, even if logout happens before the SDK has finished loading.
 * Re-applies consent afterward so the next login can be tracked.
 */
export function reset() {
  pendingCalls.length = 0
  sessionDealerGroupActive = false
  identifiedGroups.clear()
  if (initialized) {
    posthog.reset()
    syncConsent(posthog)
  }
}

// #endregion
